// src/components/auth/ForgotPasswordModals.jsx
import React, { useState } from "react";
import { Modal, Input, Button, Form, Select } from "antd";
import { MailOutlined, ReloadOutlined } from "@ant-design/icons";
import fi_2665311 from "../../assets/images/fi_2665311.png";
import { useResetPasswordSendLinkMutation } from "../../store/api/apiSlice";

const ForgotPasswordModals = ({ visible, onCancel }) => {
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [form] = Form.useForm();

  const [resetPasswordSendLink, { isLoading }] =
    useResetPasswordSendLinkMutation();

  const handleSendResetLink = () => {
    form
      .validateFields()
      .then((values) => {
        resetPasswordSendLink({ email: values.email, role: values.role })
          .unwrap()
          .then(() => {
            setIsEmailSent(true);
          })
          .catch((error) => {
            console.error("Error sending reset link:", error);
          });
      })
      .catch((err) => {
      });
  };

  const handleResendEmail = () => {
    const email = form.getFieldValue("email");
    const role = form.getFieldValue("role");

    if (email) {
      resetPasswordSendLink({ email, role: role })
        .unwrap()
        .then(() => {
          // Show success notification if needed
        })
        .catch((error) => {
          console.error("Error resending reset link:", error);
        });
    }
  };

  const handleCancel = () => {
    setIsEmailSent(false);
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      open={visible}
      footer={null}
      onCancel={handleCancel}
      width={600}
      centered
      closable={false}
      className="rounded-xl"
    >
      {!isEmailSent ? (
        <div className="flex flex-col items-center py-6 px-0">
          <div className="relative">
            <img
              src={fi_2665311}
              alt="Reset Password"
              className="h-10 w-auto"
            />
          </div>
          <h2 className="text-2xl font-semibold text-center mt-0 mb-3">
            Reset Your Password
          </h2>
          <p className="text-gray-500 text-center mb-8 max-w-md">
            Enter your email address in the field below. Click the "Send Reset
            Link" button. You will receive an email with instructions to reset
            your password. Check your email inbox (and your spam folder, just in
            case).
          </p>
          <Form form={form} layout="vertical" className="w-full ">
            <Form.Item
              label="Email Address"
              name="email"
              rules={[
                { required: true, message: "Please input your email!" },
                { type: "email", message: "Please enter a valid email!" },
              ]}
            >
              <Input
                prefix={<MailOutlined className="text-gray-400" />}
                placeholder="Please enter your email address here..."
                size="large"
                className="rounded-xl py-3"
              />
            </Form.Item>
            <Form.Item
              name="role"
              rules={[{ required: true, message: "Please select user type!" }]}
              className="mb-3"
              label="Select User"
            >
              <Select
                placeholder="Select User Type"
                className="h-12 rounded-2xl"
              >
                <Option value="admin">Admin</Option>
                <Option value="organization">Organisation</Option>
                <Option value="interpreter">Interpreter</Option>
              </Select>
            </Form.Item>
            <div className="flex flex-col sm:flex-row gap-4 mt-6">
              <Button
                onClick={handleCancel}
                className="w-full sm:flex-1 h-12 rounded-xl text-xs md:text-md"
              >
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={handleSendResetLink}
                loading={isLoading}
                className="w-full sm:flex-1 h-12 rounded-xl flex items-center justify-center text-xs md:text-md"
              >
                Send Reset Link
                <svg
                  className="ml-2"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4 10H16M16 10L10 4M16 10L10 16"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </Button>
            </div>
          </Form>
        </div>
      ) : (
        <div className="flex flex-col items-center py-6 ">
          <div>
            <svg
              className="h-10 w-auto"
              xmlns="http://www.w3.org/2000/svg"
              width="160"
              height="160"
              viewBox="0 0 160 160"
              fill="none"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M80 4C121.945 4 156 38.0554 156 80C156 121.945 121.945 156 80 156C38.0554 156 4 121.945 4 80C4 38.0554 38.0554 4 80 4ZM46.838 78.7692L64.4071 90.4843C65.7898 91.4037 67.6138 91.2924 68.8781 90.2137L116.513 49.3813C117.989 48.1133 120.195 48.2061 121.563 49.5889C122.931 50.968 122.997 53.1739 121.718 54.6382L69.816 113.955C69.0782 114.793 68.0032 115.26 66.8835 115.216C65.7677 115.175 64.7296 114.63 64.0586 113.737L41.8147 84.0781C40.7321 82.6322 40.847 80.6191 42.0889 79.3067C43.3272 77.9944 45.3328 77.7682 46.838 78.7692Z"
                fill="#2ECC71"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-semibold text-center mb-3">
            Check your email for Password Reset Instructions
          </h2>
          <p className="text-gray-500 text-center mb-8 max-w-md">
            We've sent you an email with a link to reset your password. Please
            check your inbox and follow the steps in the email. If you don't
            receive the email, check your spam or junk folder. If you still
            don't see the email, click "Resend Email" to send the reset link
            again.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 w-full px-4">
            <Button
              onClick={handleCancel}
              className="w-full sm:flex-1 h-12 rounded-xl"
            >
              Close
            </Button>
            <Button
              type="primary"
              onClick={handleResendEmail}
              loading={isLoading}
              className="w-full sm:flex-1 h-12 rounded-xl flex items-center justify-center"
            >
              Resend Email <ReloadOutlined />
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ForgotPasswordModals;
